import {ImagePreviewDialog} from '@github-ui/agent-sessions-interface/components/ImagePreviewDialog'
import {useState} from 'react'
import {useReferenceActivation} from '@github-ui/copilot-shared-ui/contexts/ReferenceActivationContext'

import type {UserMessageImage} from '../../types/user-message-image'

import styles from './UserMessageImages.module.css'

/**
 * Presentational list of image previews attached to a timeline entry. Renders
 * nothing when there are no images, so callers can render it unconditionally.
 * Activating an image opens a larger preview dialog.
 *
 * Images render at their natural size (never upscaled, never cropped) and
 * without a border, so they look consistent across every One Copilot
 * surface: user-message bubbles, tool results, and the workbench.
 */
export function UserMessageImages({images}: {images?: UserMessageImage[]}) {
  const activateReference = useReferenceActivation()
  const [previewImage, setPreviewImage] = useState<UserMessageImage | null>(null)

  if (images === undefined || images.length === 0) return null

  return (
    <span className={styles.images}>
      {images.map((image, index) => (
        <button
          key={`${image.name}-${index}`}
          type="button"
          className={styles.imageButton}
          onClick={event => {
            if (!activateReference?.({kind: 'image', image}, event)) setPreviewImage(image)
          }}
          aria-label={`Preview ${image.name}`}
        >
          <img className={styles.image} src={image.src} alt={image.alt ?? image.name} />
        </button>
      ))}
      {previewImage && (
        <ImagePreviewDialog
          src={previewImage.src}
          title={previewImage.name}
          alt={previewImage.alt ?? previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </span>
  )
}
